import { State, Action, Selector, StateContext } from '@ngxs/store';
import { DecodedToken, LastLoggedInUser, SnackbarType } from '@core/models';
import {
    LoginAction,
    LoginSuccessAction,
    LoginFailAction,
    ClearTokenAction,
    ClearLastUserAction,
    RequestForgottenPasswordAction,
    RequestForgottenPasswordSuccessAction,
    RequestForgottenPasswordFailAction,
    ResetForgottenPasswordAction,
    ResetForgottenPasswordSuccessAction,
    ResetForgotenPasswordFailAction,
    ChangeTenantAction
} from './auth.actions';
import { tap, catchError } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { OpenSnackbarAction, OpenToastrAction } from '../ui-store/ui.actions';
import { Navigate } from '@ngxs/router-plugin';
import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import { UsersClient, IssuedTokenModel } from '@core/services/api.service';
import { TenantModel } from '@core/models/auth/tenant.model';
import { ToastrService } from 'ngx-toastr';
import { LoadCartItemsAction } from '@ngxs-store/cart/cart.actions';
import { LoadWishListAction } from '@ngxs-store/wish-list-store/wish-list.actions';
import { ToastrType } from '@core/models/ui/toastr-config';


export class AuthStateModel {
    public isLoading: boolean;
    public lastLoggedInUser: LastLoggedInUser;
    public decodedToken: DecodedToken;
    public tenant: TenantModel;
}

@State<AuthStateModel>({
    name: 'auth',
    defaults: {
        isLoading: false,
        lastLoggedInUser: { username: '', firstName: '', surname: '' },
        decodedToken: {
            firstName: '',
            expires: null,
            roles: [],
            surname: '',
            token: '',
            username: '',
            uid: '',
            tenants: []
        },
        tenant: {
            id: '',
            name: ''
        }
    }
})
@Injectable()
export class AuthState {
    jwtHelper = new JwtHelperService();

    @Selector()
    static getIsLoading(state: AuthStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static getDecodedToken(state: AuthStateModel): DecodedToken {
        return state.decodedToken;
    }

    @Selector()
    static getLastLoggedInUser(state: AuthStateModel): LastLoggedInUser {
        return state.lastLoggedInUser;
    }

    @Selector()
    static getTenant(state: AuthStateModel): TenantModel {
        return state.tenant;
    }

    @Selector()
    static getHasValidToken(state: AuthStateModel): boolean {
        return state.decodedToken.token !== '' && state.decodedToken.expires !== null && new Date() < new Date(state.decodedToken.expires) ? true : false;
    }

    @Selector()
    static getHasRole(state: AuthStateModel) {
        return (requiredRoles: string[]) => {
            return requiredRoles.length === 0 ? true : state.decodedToken.roles.some((r) => requiredRoles.indexOf(r) >= 0);
        };
    }

    constructor(
        private usersClient: UsersClient, private toaster: ToastrService
    ) { }

    @Action(LoginAction)
    loginAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoginAction) {
        patchState({ isLoading: true });

        return this.usersClient.authenticateUser(action.payload.authenticateUserCommand).pipe(
            tap((encodedToken) => dispatch(new LoginSuccessAction({ decodedToken: this.decodeToken(encodedToken), redirectUrl: action.payload.redirectUrl }))),
            catchError((err) => dispatch(new LoginFailAction()))
        );
    }

    @Action(LoginSuccessAction)
    loginSuccessAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoginSuccessAction) {

        const firstTenant = action.payload.decodedToken;

        // const tenant = { id: firstTenant.split('|')[0], name: firstTenant.split('|')[1] };

        patchState({
            decodedToken: action.payload.decodedToken,
            lastLoggedInUser: {
                username: action.payload.decodedToken.username,
                firstName: action.payload.decodedToken.firstName,
                surname: action.payload.decodedToken.surname
            },
            isLoading: false,

            //   tenant
        });

        dispatch([
            new LoadCartItemsAction(),
            new LoadWishListAction()
        ]);

        if (action.payload.redirectUrl) {
            dispatch(new Navigate([action.payload.redirectUrl]));
        }
        dispatch(new OpenToastrAction({ message: 'You have successfully logged in', type: ToastrType.success, title: 'Login' }));

    }

    @Action(LoginFailAction)
    loginFailAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoginFailAction) {
        patchState({
            decodedToken: {
                firstName: '',
                expires: null,
                roles: [],
                surname: '',
                token: '',
                username: '',
                uid: '',
                tenants: []
            },
            isLoading: false,
            tenant: {
                id: '',
                name: ''
            }
        });
    }

    @Action(ClearTokenAction)
    clearTokenAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ClearTokenAction) {
        patchState({
            decodedToken: {
                firstName: '',
                expires: null,
                roles: [],
                surname: '',
                token: '',
                username: '',
                uid: '',
                tenants: []
            },
            tenant: {
                id: '',
                name: ''
            }
        });

        if (environment.routeOnLogout.enabled) {
            dispatch(new Navigate(environment.routeOnLogout.url));
        }
    }

    @Action(ClearLastUserAction)
    clearLastUserAction({ patchState }: StateContext<AuthStateModel>, action: ClearLastUserAction) {
        patchState({
            lastLoggedInUser: { username: '', firstName: '', surname: '' }

        });
        patchState({
            lastLoggedInUser: { username: '', firstName: '', surname: '' }

        });
    }

    decodeToken(issuedTokenModel: IssuedTokenModel): DecodedToken {
        const decoded = this.jwtHelper.decodeToken(issuedTokenModel.token);

        return {
            expires: new Date(decoded.exp * 1000).toISOString(),
            firstName: decoded.firstName,
            roles: decoded.roles instanceof Array ? decoded.roles : [decoded.roles],
            username: decoded.sub.replace('SUPERGRP\\', ''),
            surname: decoded.surname,
            token: issuedTokenModel.token,
            uid: decoded.uid,
            tenants: decoded.tenants instanceof Array ? decoded.tenants : [decoded.tenants]
        };
    }

    @Action(RequestForgottenPasswordAction)
    RequestForgottenPasswordAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: RequestForgottenPasswordAction) {
        patchState({ isLoading: true });

        return this.usersClient.requestPasswordReset(action.payload.requestPasswordResetCommand).pipe(
            tap((email) => dispatch(new RequestForgottenPasswordSuccessAction({ email: email.emailAddress }))),
            catchError((err) => dispatch(new RequestForgottenPasswordFailAction()))
        );
    }

    @Action(RequestForgottenPasswordSuccessAction)
    RequestForgottenPasswordSuccessAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: RequestForgottenPasswordSuccessAction) {
        patchState({
            isLoading: false
        });

        dispatch(new Navigate(['/login']));
      //  dispatch(new OpenSnackbarAction({ message: 'Password reset email sent.', type: SnackbarType.Simple }));
      //  this.toaster.success("Password reset email sent.", "Password Reset Email", { timeOut: 3000 });
    }

    @Action(RequestForgottenPasswordFailAction)
    RequestForgottenPasswordFailAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: RequestForgottenPasswordFailAction) {
        patchState({
            isLoading: false
        });
    }

    @Action(ResetForgottenPasswordAction)
    ResetForgottenPasswordAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: ResetForgottenPasswordAction) {
        patchState({ isLoading: true });

        return this.usersClient.resetPassword(action.payload.resetPasswordCommand).pipe(
            tap((username) => dispatch(new ResetForgottenPasswordSuccessAction({ username: username.username }))),
            catchError((err) => dispatch(new ResetForgotenPasswordFailAction()))
        );
    }

    @Action(ResetForgottenPasswordSuccessAction)
    ResetForgottenPasswordSuccessAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ResetForgottenPasswordSuccessAction) {
        patchState({
            isLoading: false
        });

        dispatch(new Navigate(['/login']));
        this.toaster.success("Password successfully reset.", "Password Reset", { timeOut: 3000 });
    }

    @Action(ResetForgotenPasswordFailAction)
    resetForgotenPasswordFailAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ResetForgotenPasswordFailAction) {
        patchState({
            isLoading: false
        });
    }

    @Action(ChangeTenantAction)
    changeTenantAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ChangeTenantAction) {
        const state = getState();
        patchState({
            tenant: action.payload.tenant
        });

        location.reload();
    }

}
